import { isToolUIPart, getToolName } from "ai";
import { useEffect } from "react";
import { useStickToBottom } from "use-stick-to-bottom";

import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import usePrevious from "hooks/usePrevious";
import MarkdownAssistantPanel from "components/markdown/MarkdownAssistantPanel";
import { IconComponent } from "types/Icon";

import { AiChatMessage } from "../types";
import AvatarAnimated from "../Avatar/Animated";
import AvatarStatic from "../Avatar/Static";
import Thinking from "../Thinking";
import AiChatToolCall from "../Tool";
import { ToolPart } from "../Tool/helpers";
import styles from "./styles.module.css";

type AiChatMessagesProps = {
  messages: AiChatMessage[];
  isStreaming: boolean;
  toolIcons?: Record<string, IconComponent>;
  onApproveTool?: (part: ToolPart) => void;
  onRejectTool?: (part: ToolPart) => void;
};

const AiChatMessages = ({
  messages,
  isStreaming,
  toolIcons,
  onApproveTool,
  onRejectTool,
}: AiChatMessagesProps) => {
  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom({
    initial: "instant",
    resize: "smooth",
  });

  const userMessagesCount = messages.filter((m) => m.role === "user").length;
  const prevUserMessagesCount = usePrevious(userMessagesCount);

  useEffect(() => {
    if (prevUserMessagesCount !== undefined && userMessagesCount > prevUserMessagesCount) {
      scrollToBottom();
    }
  }, [userMessagesCount, prevUserMessagesCount, scrollToBottom]);

  const lastMessage = messages.at(-1);
  const isLastMessageFromAssistant = lastMessage?.role === "assistant";
  const lastAssistantMessageId = isLastMessageFromAssistant ? lastMessage.id : undefined;

  return (
    <div ref={scrollRef} className={styles.scrollContainer}>
      <Box ref={contentRef} direction="column" gap="xx-large" padding="x-large">
        {messages.map((message) => {
          if (message.role === "user") {
            return (
              <Box key={message.id} gap="medium" justify="end" align="end">
                <Box className={styles.userMessageContent}>
                  {message.parts.map((part, index) => {
                    if (part.type === "text") {
                      return (
                        <Typography key={index} tag="p" variant="p-body3-multiline">
                          {part.text}
                        </Typography>
                      );
                    }
                    return null;
                  })}
                </Box>
              </Box>
            );
          }

          const showAnimatedAvatar = isStreaming && message.id === lastAssistantMessageId;

          return (
            <Box key={message.id} gap="medium" className={styles.assistantMessage}>
              {showAnimatedAvatar ? <AvatarAnimated /> : <AvatarStatic />}
              <Box direction="column" gap="x-large" padding="small 0 0" zeroMinWidth grow="1">
                {message.parts.map((part, index) => {
                  switch (part.type) {
                    case "reasoning":
                      return (
                        <Thinking
                          key={index}
                          text={part.text}
                          isStreaming={part.state === "streaming"}
                        />
                      );
                    case "text":
                      return (
                        <MarkdownAssistantPanel key={index}>{part.text}</MarkdownAssistantPanel>
                      );
                    case "step-start":
                    case "reasoning-file":
                    case "custom":
                      return null;
                    case "source-url":
                      return (
                        <Typography key={index} tag="p" variant="p-body3" color="secondary">
                          {part.title || part.url}
                        </Typography>
                      );
                    case "source-document":
                      return (
                        <Typography key={index} tag="p" variant="p-body3" color="secondary">
                          {part.title}
                        </Typography>
                      );
                    case "file":
                      return part.mediaType.startsWith("image/") ? (
                        <img
                          key={index}
                          src={part.url}
                          alt={part.filename || "file"}
                          className={styles.fileImage}
                        />
                      ) : (
                        <Typography key={index} tag="p" variant="p-body3" color="secondary">
                          {part.filename || part.mediaType}
                        </Typography>
                      );
                    case "dynamic-tool": {
                      const toolName = getToolName(part);
                      return (
                        <AiChatToolCall
                          key={index}
                          className={styles.toolCall}
                          icon={toolIcons?.[toolName]}
                          name={toolName}
                          part={part}
                          onApprove={onApproveTool ? () => onApproveTool(part) : undefined}
                          onReject={onRejectTool ? () => onRejectTool(part) : undefined}
                        />
                      );
                    }
                    default: {
                      if (isToolUIPart(part)) {
                        const toolName = getToolName(part);
                        return (
                          <AiChatToolCall
                            key={index}
                            className={styles.toolCall}
                            icon={toolIcons?.[toolName]}
                            name={toolName}
                            part={part}
                            onApprove={onApproveTool ? () => onApproveTool(part) : undefined}
                            onReject={onRejectTool ? () => onRejectTool(part) : undefined}
                          />
                        );
                      }
                      return null;
                    }
                  }
                })}
              </Box>
            </Box>
          );
        })}

        {isStreaming && lastMessage?.role === "user" && (
          <Box gap="medium" align="start" className={styles.assistantMessage}>
            <AvatarAnimated />
          </Box>
        )}
      </Box>
    </div>
  );
};

export default AiChatMessages;
